// ResponseFlags — messages the office has flagged "response required".
//
// v0.321.0 (2026-11-04). Brett: "I need a button and indicator that screams
// out that there is an outstanding response required. It would display on
// this page and the register." Then: "Could we have like a 3 dot button to
// the comment line and we could flag as response required".
//
// Any office person flags a message from its ... menu (DefectConversation).
// The flag stays until someone presses Clear flag on that ...: a reply does
// NOT clear it, so "will check" never makes it vanish. (v0.322.0: the card's
// red banner with Mark responded is gone - Brett: "just the redbox is fine".)
//
//   useResponseFlags(projectId) — the items in a project holding a flagged
//     message. Read when the screen opens, after a flag or clear in this tab
//     and when the app comes back to the front. No timer: the database pauses
//     after an hour on purpose and a timer would keep waking it.
//   ResponseRequiredChip — the register row's marker.
//   ResponseFlagsReadError — a failed read, said in words (no chip must never
//     pass for "nothing flagged"). The register's red line above the filters
//     (v0.321.0) is gone: Brett, v0.322.0, "remove this one too".
//
// Office only. Client and subcontractor sign-ins never ask; the route refuses
// them. v0.322.0: a client or subbie sees the flags in their OWN chat on their
// report link and can flag back (api/public-flag); those count here too.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Flag } from 'lucide-react';
import { apiGet } from '../api';
import { theme, btnPrimary } from '../theme';
import { isClientContactSession } from '../utils/contact-session';

export const FLAGS_CHANGED_EVENT = 'droppin:flags-changed';

/** Tell every flag reader in this tab to read again. */
export function announceFlagsChanged(defectId) {
  try { window.dispatchEvent(new CustomEvent(FLAGS_CHANGED_EVENT, { detail: { defectId } })); } catch (_) {}
}

const READ_FAILED = 'Could not check for items needing a response.';

function isAuthEnd(e) { return !!(e && (e.status === 401 || e.status === 403)); }

/**
 * { items: [{ defectId, reportId, flags, flaggedAt, flaggedByName }], byDefect, readErr }
 * for one project. Empty for a client or subcontractor sign-in.
 */
export function useResponseFlags(projectId) {
  const [items, setItems] = useState([]);
  const [readErr, setReadErr] = useState('');
  const seq = useRef(0);
  const skip = isClientContactSession();

  const load = useCallback(async () => {
    if (!projectId || skip) return;
    const mine = ++seq.current;
    try {
      const r = await apiGet(`/api/comment-flag?projectId=${encodeURIComponent(projectId)}`, { fresh: true });
      if (mine !== seq.current) return;           // a newer read is on its way
      setItems(r && Array.isArray(r.items) ? r.items : []);
      setReadErr('');
    } catch (e) {
      if (mine !== seq.current) return;
      if (isAuthEnd(e)) { setItems([]); setReadErr(''); return; }
      setReadErr(READ_FAILED);
      console.warn('[DropPin] response flags read failed:', (e && e.message) || e);
    }
  }, [projectId, skip]);

  useEffect(() => { setItems([]); setReadErr(''); load(); }, [load]);
  useEffect(() => {
    const onChanged = () => load();
    const onVisible = () => { if (document.visibilityState === 'visible') load(); };
    window.addEventListener(FLAGS_CHANGED_EVENT, onChanged);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      window.removeEventListener(FLAGS_CHANGED_EVENT, onChanged);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [load]);

  const byDefect = useMemo(() => new Map(items.map((x) => [Number(x.defectId), x])), [items]);
  return { items, byDefect, readErr };
}

/** Filled red, so it reads apart from the orange New reply chip beside it. */
export function ResponseRequiredChip({ flag }) {
  if (!flag) return null;
  const who = flag.flaggedByName ? ` by ${flag.flaggedByName}` : '';
  return (
    <span
      data-testid="response-required-chip"
      title={`Flagged${who}: a response is required. Open the item to see the message.`}
      style={{
        flexShrink: 0,
        display: 'inline-flex', alignItems: 'center', gap: 3,
        fontSize: 10, fontWeight: 700,
        padding: '2px 7px', borderRadius: 999,
        background: theme.danger, color: btnPrimary.color,
        border: `1px solid ${theme.danger}`,
        whiteSpace: 'nowrap',
      }}
    >
      <Flag size={11} strokeWidth={2.4} aria-hidden="true" />
      Response required
    </span>
  );
}

/** A failed read of the flags, in words, muted (never a red box). */
export function ResponseFlagsReadError({ readErr }) {
  if (!readErr) return null;
  return <div data-testid="response-flags-read-error" style={{ marginTop: 14, fontSize: 12, color: theme.textLight }}>{readErr}</div>;
}
